import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { QaroniIntermediate } from '@qaroni-core/types/qaroni-intermediate/qaroni-intermediate';
import { Subject } from 'rxjs';
import {
  PermitApproval,
  PermitApprovalAssignDTO,
} from '../types/permit-approval';
import {
  PermitApprovalCounter,
  PermitApprovalCounterApiObject,
} from '../types/permit-approval-counter';
import { PermitApprovalApiObject } from '../types/permit-approval-response';
import { PermitsApprovalsHttpService } from './permits-approvals-http.service';
import { PermitsApprovalsSnackbarsService } from './permits-approvals-snackbars.service';

@Injectable({
  providedIn: 'root',
})
export class PermitsApprovalsService extends QaroniIntermediate {
  private readonly http = inject(PermitsApprovalsHttpService);
  private readonly snackbars = inject(PermitsApprovalsSnackbarsService);

  private readonly approvalsSubject = new Subject<PermitApproval[]>();
  private readonly approvalSubject = new Subject<PermitApproval | null>();
  private readonly flowSubject = new Subject<PermitApproval[]>();
  private readonly counterSubject = new Subject<PermitApprovalCounter | null>();

  public readonly approvals$ = this.approvalsSubject.asObservable();
  public readonly approval$ = this.approvalSubject.asObservable();
  public readonly flow$ = this.flowSubject.asObservable();
  public readonly counter$ = this.counterSubject.asObservable();

  public list(companyID: number, params?: Params): void {
    this.http
      .list$(companyID, params)
      .subscribe({ next: this.nextList, error: this.errorList });
  }

  private nextList = (response: PermitApprovalApiObject): void => {
    this.approvalsSubject.next(response?.result ?? []);
    this.linksSubject.next(response?.links ?? null);
  };

  private errorList = (error: HttpErrorResponse): void => {
    this.approvalsSubject.next([]);
    this.linksSubject.next(null);
    this.errors.communication(error);
  };

  public counter(companyID: number): void {
    if (!this.hasOAuth) {
      return;
    }

    this.http
      .counter$(companyID)
      .subscribe({ next: this.nextCounter, error: this.errorCounter });
  }

  private nextCounter = (response: PermitApprovalCounterApiObject): void => {
    this.counterSubject.next(response?.result?.[0] ?? null);
  };

  private errorCounter = (): void => {
    this.counterSubject.next(null);
  };

  public approve(companyID: number, approvalID: number): void {
    if (!this.hasOAuth) {
      return;
    }

    this.http
      .approve$(companyID, approvalID)
      .subscribe({ next: this.nextApprove, error: this.errorApprove });
  }

  private nextApprove = (response: PermitApprovalApiObject): void => {
    this.approvalSubject.next(response?.result?.[0] ?? null);
    this.snackbars.successApprove();
  };

  private errorApprove = (error: HttpErrorResponse): void => {
    this.approvalSubject.next(null);
    this.notifyResolveError(error);
    this.errors.communication(error);
  };

  public reject(companyID: number, approvalID: number): void {
    if (!this.hasOAuth) {
      return;
    }

    this.http
      .reject$(companyID, approvalID)
      .subscribe({ next: this.nextReject, error: this.errorReject });
  }

  private nextReject = (response: PermitApprovalApiObject): void => {
    this.approvalSubject.next(response?.result?.[0] ?? null);
    this.snackbars.successReject();
  };

  private errorReject = (error: HttpErrorResponse): void => {
    this.approvalSubject.next(null);
    this.notifyResolveError(error);
    this.errors.communication(error);
  };

  public getFlow(companyID: number, employeeAbsenceID: number): void {
    this.http
      .flow$(companyID, employeeAbsenceID)
      .subscribe({ next: this.nextGetFlow, error: this.errorGetFlow });
  }

  private nextGetFlow = (response: PermitApprovalApiObject): void => {
    this.flowSubject.next(response?.result ?? []);
  };

  private errorGetFlow = (error: HttpErrorResponse): void => {
    this.flowSubject.next([]);
    this.errors.communication(error);
  };

  public assign(
    companyID: number,
    approvalID: number,
    assignment: PermitApprovalAssignDTO
  ): void {
    if (!this.hasOAuth) {
      return;
    }

    this.http
      .assign$(companyID, approvalID, assignment)
      .subscribe({ next: this.nextAssign, error: this.errorAssign });
  }

  private nextAssign = (response: PermitApprovalApiObject): void => {
    this.approvalSubject.next(response?.result?.[0] ?? null);
    this.snackbars.successAssign();
  };

  private errorAssign = (error: HttpErrorResponse): void => {
    this.approvalSubject.next(null);

    if (this.errors.isControlledError(error, 'E0004')) {
      this.snackbars.failureAssignValidation();
    } else if (this.errors.isControlledError(error, 'E0008')) {
      this.snackbars.failureNotCompanyAdmin();
    } else if (this.errors.isControlledError(error, 'E0108')) {
      this.snackbars.failureAlreadyResolved();
    } else if (this.errors.isControlledError(error, 'E0110')) {
      this.snackbars.failureInvalidApprover();
    }

    this.errors.communication(error);
  };

  private notifyResolveError(error: HttpErrorResponse): void {
    if (this.errors.isControlledError(error, 'E0004')) {
      this.snackbars.failureValidation();
    } else if (this.errors.isControlledError(error, 'E0042')) {
      this.snackbars.failureNotEnoughBalance();
    } else if (this.errors.isControlledError(error, 'E0081')) {
      this.snackbars.failureOutsideGrantWindow();
    } else if (this.errors.isControlledError(error, 'E0102')) {
      this.snackbars.failureGrantClosed();
    } else if (this.errors.isControlledError(error, 'E0106')) {
      this.snackbars.failureNotActive();
    } else if (this.errors.isControlledError(error, 'E0107')) {
      this.snackbars.failureNotAssigned();
    } else if (this.errors.isControlledError(error, 'E0108')) {
      this.snackbars.failureAlreadyResolved();
    }
  }
}
